import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as fromPE from '../fromPe.barrel';
import { catchError, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { PeService } from 'src/app/services/pe.service';
import { odataParamsFactory } from '@tdw/core';
import { Store } from '@ngrx/store';
import { Application, Measurement } from 'src/app/models';


@Injectable()
export class PeEffects {
  constructor(
    private store: Store<fromPE.State>,
    private actions$: Actions,
    private peService: PeService) { }

  loadPipeSizes$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.loadPipeSizes),
    switchMap(action => this.peService.getPipeSizes(odataParamsFactory({ orderBy: 'SortOrder' }))
      .pipe(map(res => fromPE.loadPipeSizesSuccess({ pipeSizes: res.value || [] })),
        catchError(error => of(fromPE.loadPipeSizesFail({ error })))))));

  loadFittings$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.loadFittings),
    withLatestFrom(this.store.select(fromPE.selectFilter)),
    map(([action, peFilter]) => {
      let str = '';
      if (peFilter.application !== Application.Any) str += ` AND ApplicationType eq '${peFilter.application}'`;
      if (!!peFilter.measurement && peFilter.measurement !== Measurement.Any) str += ` AND HemisphereID eq ${peFilter.measurement}`;
      if (peFilter.sizes.length > 0) str += ` AND PipeSizeID in (${peFilter.sizes.toString()})`;
      if (str.startsWith(' AND ')) str = str.substring(5);
      return str;
    }),
    switchMap(filterStr => this.peService.loadPEFittingProducts(odataParamsFactory({ filter: filterStr }))
      .pipe(map(res => fromPE.loadFittingsSuccess({ fittings: res.value || [] })),
        catchError(error => of(fromPE.loadFittingsFail({ error })))))
  ))

  loadEquipment$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.loadEquipment),
    withLatestFrom(this.store.select(fromPE.selectFilter)),
    map(([action, peFilter]) => {
      let q = `SubfamilyID eq null AND (${peFilter?.familyID || -1} eq -1 OR FamilyID eq ${peFilter?.familyID || -1})`;
      if (peFilter.sizes.length > 0) q += ` AND PEEquipmentSizes/any(sz: sz/peSizeID in (${peFilter.sizes.map(s => s).toString()}))`;
      return q;
    }),
    switchMap(q => this.peService.loadEquipment(odataParamsFactory({
      // 6 === Tapping Machine
      // 9 === Plugging Machine
      // filter: `SubfamilyID eq null AND (${peFilter?.familyID || -1} eq -1 OR FamilyID eq ${peFilter?.familyID || -1})`,
      expand: 'EquipmentFamily,SubEquipmentFamily,PEEquipmentSizes',
      filter: q
    }))
      .pipe(
        map(res => fromPE.loadEquipmentSuccess({ equipment: res.value || [] })),
        catchError(error => of(fromPE.loadEquipmentFail({ error })))))));

  loadAccessories$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.loadAccessory),
    withLatestFrom(this.store.select(fromPE.selectFilter)),
    map(([action, peFilter]) => {
      console.log(peFilter.sizes);
      let q = `(${(peFilter?.familyID || -1)} eq -1 OR FamilyID eq ${peFilter?.familyID || -1}) AND (${peFilter?.subFamilyID || -1} eq -1 OR SubFamilyID eq ${peFilter?.subFamilyID || -1})`;
      if (peFilter.sizes.length > 0) q += ` AND PEEquipmentSizes/any(sz: sz/peSizeID in (${peFilter.sizes.map(s => s).toString()}))`;
      return q;
    }),
    switchMap(q => this.peService.loadEquipment(odataParamsFactory({
      filter: q,
      expand: 'EquipmentFamily,SubEquipmentFamily,PEEquipmentSizes',
      orderBy: 'FamilyId'
    })).pipe(
      map(res => fromPE.loadAccessorySuccess({ accessories: res.value || [] })),
      catchError(error => of(fromPE.loadAccessoryFail({ error })))))));

  loadEquipmentFamilies$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.loadEquipmentFamilies),
    switchMap(action => this.peService.loadEquipmentFamilies(odataParamsFactory({ expand: 'ChildEquipment', orderBy: 'text' }))
      .pipe(map(res => fromPE.loadEquipmentFamiliesSuccess({ families: res.value || [] })),
        catchError(error => of(fromPE.loadEquipmentFamiliesFail({ error })))))
  ));

  loadFittingsOnSelectionChange$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.updatePeFilter, fromPE.toggleSizeOnFilter),
    withLatestFrom(this.store.select(fromPE.selectFilter)),
    mergeMap(([action, peFilter]) => [
      fromPE.updateEquipmentSelections({ obj: peFilter }),
      fromPE.loadFittings(),
      fromPE.loadAccessory()
    ])
  ));

  loadEquipmentOnSelectionChange$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.updateEquipmentSelections),
    map(action => fromPE.loadEquipment())
  ));

  loadAccessoryOnSelectionChange$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.updateAccessorySelections),
    map(action => fromPE.loadAccessory())
  ));

  exportPDF$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.exportPDF),
    withLatestFrom(
      this.store.select(fromPE.selectActiveFittings),
      this.store.select(fromPE.selectActiveEquipment),
      this.store.select(fromPE.selectActiveAccessories)
    ),
    switchMap(([action, fittings, equipment, accessories]) => this.peService.loadReport(
      'pdf',
      fittings.map(m => m.partNumber).toString().replace(/,/g, ';'),
      equipment.map(m => m.partNumber).toString().replace(/,/g, ';'),
      accessories.map(m => m.partNumber).toString().replace(/,/g, ';')
    )
      .pipe(map(file => {
        window.open(URL.createObjectURL(file));
        return fromPE.exportPDFSuccess()
      }), catchError(error => of(fromPE.exportPDFFail({ error })))))
  ));

  exportExcel$ = createEffect(() => this.actions$.pipe(
    ofType(fromPE.exportExcel),
    withLatestFrom(
      this.store.select(fromPE.selectActiveFittings),
      this.store.select(fromPE.selectActiveEquipment),
      this.store.select(fromPE.selectActiveAccessories)
    ),
    switchMap(([action, fittings, equipment, accessories]) => this.peService.loadReport(
      'Excel',
      fittings.map(m => m.partNumber).toString().replace(/,/g, ';'),
      equipment.map(m => m.partNumber).toString().replace(/,/g, ';'),
      accessories.map(m => m.partNumber).toString().replace(/,/g, ';')
    )
      .pipe(map(file => {
        window.open(URL.createObjectURL(file));
        return fromPE.exportExcelSuccess()
      }), catchError(error => of(fromPE.exportExcelFail({ error })))))
  ));


}
